import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Receipt } from "lucide-react";
import axios from "axios";

const MONTH_NAMES = { "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile", "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto", "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre" };

const EUR = (v) => `\u20AC${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = -3; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${MONTH_NAMES[String(d.getMonth() + 1).padStart(2, "0")]} ${d.getFullYear()}`;
    opts.push({ value: val, label });
  }
  return opts;
}

const emptyForm = { property_id: "", month: "", nome: "", importo: 0, tipo: "fisso" };

export default function CostsPage() {
  const { user, API } = useAuth();
  const [costs, setCosts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterProp, setFilterProp] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");

  const isAdmin = user?.role === "admin";
  const monthOptions = useMemo(() => buildMonthOptions(), []);

  useEffect(() => {
    Promise.all([axios.get(`${API}/costs`), axios.get(`${API}/properties`)])
      .then(([c, p]) => { setCosts(c.data); setProperties(p.data); })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [API]);

  const filtered = useMemo(() => {
    let result = costs;
    if (filterProp !== "all") result = result.filter(c => c.property_id === filterProp);
    if (filterMonth !== "all") result = result.filter(c => c.month === filterMonth);
    return result;
  }, [costs, filterProp, filterMonth]);

  const totalFiltered = useMemo(() => filtered.reduce((a, c) => a + (c.importo || 0), 0), [filtered]);

  const openAdd = () => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setEditing(null);
    setForm({ ...emptyForm, month: currentMonth });
    setDialogOpen(true);
  };
  const openEdit = (c) => { setEditing(c); setForm({ property_id: c.property_id, month: c.month, nome: c.nome, importo: c.importo, tipo: c.tipo }); setDialogOpen(true); };

  const handleSave = async () => {
    const payload = { ...form, importo: Number(form.importo) };
    try {
      if (editing) {
        const res = await axios.put(`${API}/costs/${editing.id}`, payload);
        setCosts(prev => prev.map(c => c.id === editing.id ? res.data : c));
        toast.success("Costo aggiornato");
      } else {
        const res = await axios.post(`${API}/costs`, payload);
        setCosts(prev => [res.data, ...prev]);
        toast.success("Costo aggiunto");
      }
      setDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo costo?")) return;
    try { await axios.delete(`${API}/costs/${id}`); setCosts(prev => prev.filter(c => c.id !== id)); toast.success("Costo eliminato"); } catch { toast.error("Errore"); }
  };

  const fmtMonth = (m) => {
    if (!m) return "";
    const parts = m.split("-");
    return `${MONTH_NAMES[parts[1]] || parts[1]} ${parts[0]}`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="costs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Costi Appartamenti</h1>
          <p className="text-stone-500 mt-1 text-sm">Gestisci costi fissi e variabili per ogni appartamento</p>
        </div>
        {isAdmin && <Button onClick={openAdd} data-testid="add-cost-button" className="bg-stone-900 hover:bg-stone-800 text-white"><Plus className="h-4 w-4 mr-2" /> Aggiungi Costo</Button>}
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <Select value={filterProp} onValueChange={setFilterProp}>
          <SelectTrigger data-testid="costs-filter-property" className="w-52 bg-white"><SelectValue placeholder="Appartamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli appartamenti</SelectItem>
            {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger data-testid="costs-filter-month" className="w-48 bg-white"><SelectValue placeholder="Mese" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i mesi</SelectItem>
            {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-stone-100 rounded-sm border border-stone-200">
          <span className="text-xs text-stone-500">Totale filtrato:</span>
          <span className="font-mono text-sm font-semibold text-stone-900">{EUR(totalFiltered)}</span>
        </div>
      </div>

      {costs.length === 0 && filterProp === "all" && filterMonth === "all" ? (
        <Card className="border border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Receipt className="h-12 w-12 text-stone-300 mx-auto mb-4" strokeWidth={1} />
            <p className="text-stone-500">Nessun costo registrato</p>
            <p className="text-xs text-stone-400 mt-1">Aggiungi costi fissi (es. WiFi) e variabili (es. spese extra) per ogni appartamento</p>
            {isAdmin && <Button onClick={openAdd} className="mt-4 bg-stone-900 text-white">Aggiungi il primo costo</Button>}
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-stone-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="text-xs font-medium text-stone-500">Appartamento</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Mese</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Nome Costo</TableHead>
                <TableHead className="text-xs font-medium text-stone-500">Tipo</TableHead>
                <TableHead className="text-xs font-medium text-stone-500 text-right">Importo</TableHead>
                {isAdmin && <TableHead className="text-xs w-20" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-stone-400 py-8">Nessun costo per i filtri selezionati</TableCell></TableRow>
              ) : filtered.map(c => (
                <TableRow key={c.id} data-testid={`cost-row-${c.id}`} className="hover:bg-stone-50 transition-colors">
                  <TableCell className="text-sm text-stone-600">{c.property_name}</TableCell>
                  <TableCell className="text-sm text-stone-600">{fmtMonth(c.month)}</TableCell>
                  <TableCell className="text-sm font-medium text-stone-900">{c.nome}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${c.tipo === "fisso" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-orange-50 text-orange-700 border-orange-200"}`}>
                      {c.tipo === "fisso" ? "Fisso" : "Variabile"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm font-mono text-stone-900 text-right font-medium">{EUR(c.importo)}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5 text-stone-500" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Modifica Costo" : "Nuovo Costo"}</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">{editing ? "Modifica il costo dell'appartamento" : "Inserisci un costo fisso o variabile per l'appartamento"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-stone-600">Appartamento</Label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="cost-property-select" className="mt-1 bg-white"><SelectValue placeholder="Seleziona appartamento" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Mese di riferimento</Label>
              <Select value={form.month} onValueChange={v => setForm({ ...form, month: v })}>
                <SelectTrigger data-testid="cost-month-select" className="mt-1 bg-white"><SelectValue placeholder="Seleziona mese" /></SelectTrigger>
                <SelectContent>{monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Nome Costo</Label>
              <Input data-testid="cost-nome-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="Es. WiFi, Utenze, Spesa extra..." className="mt-1 bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-stone-600">Importo ({"\u20AC"})</Label>
                <Input data-testid="cost-importo-input" type="number" step="0.01" min={0} value={form.importo} onChange={e => setForm({ ...form, importo: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="cost-tipo-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fisso">Fisso (mensile)</SelectItem>
                    <SelectItem value="variabile">Variabile (extra)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSave} data-testid="cost-save-button" className="w-full bg-stone-900 hover:bg-stone-800 text-white">
              {editing ? "Salva Modifiche" : "Aggiungi Costo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
